import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMyTickets, usePurchaseHistory } from '../../hooks/useTickets';
import { Container } from '../../components/layout/Container';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Spinner } from '../../components/common/Spinner';
import { formatCurrency, formatDate, formatPercentage } from '../../utils/formatters';
import { Ticket as TicketIcon, Trophy, ShoppingCart, Calendar, X, Search } from 'lucide-react';
import { Ticket } from '../../types';

type TabType = 'all' | 'purchases' | 'wins';

export const MyTickets: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [selectedRaffleId, setSelectedRaffleId] = useState('');
  
  const statusFilter = activeTab === 'wins' ? 'WON' : undefined;
  const { data, isLoading } = useMyTickets({ 
    page, limit: 20, status: statusFilter, raffleId: selectedRaffleId || undefined,
    startDate: appliedStartDate ? new Date(appliedStartDate).toISOString() : undefined,
    endDate: appliedEndDate ? new Date(appliedEndDate + 'T23:59:59').toISOString() : undefined,
  });
  const { data: purchaseData, isLoading: purchaseLoading } = usePurchaseHistory({ page, limit: 20 });
  const { data: allTicketsForFilter } = useMyTickets({ page: 1, limit: 1000 });
  const { data: allTicketsData } = useMyTickets({ 
    page: 1, limit: 1, raffleId: selectedRaffleId || undefined,
    startDate: appliedStartDate ? new Date(appliedStartDate).toISOString() : undefined,
    endDate: appliedEndDate ? new Date(appliedEndDate + 'T23:59:59').toISOString() : undefined,
  });
  const { data: winsData } = useMyTickets({ 
    page: 1, limit: 1, status: 'WON', raffleId: selectedRaffleId || undefined,
    startDate: appliedStartDate ? new Date(appliedStartDate).toISOString() : undefined,
    endDate: appliedEndDate ? new Date(appliedEndDate + 'T23:59:59').toISOString() : undefined,
  });

  const handleApplyFilters = () => { setAppliedStartDate(startDate); setAppliedEndDate(endDate); setPage(1); };
  const handleResetFilters = () => { setStartDate(''); setEndDate(''); setAppliedStartDate(''); setAppliedEndDate(''); setSelectedRaffleId(''); setPage(1); };

  if (isLoading || (activeTab === 'purchases' && purchaseLoading)) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <Container className="py-12"><Spinner size="lg" /></Container>
      </div>
    );
  }

  const tickets = data?.data?.tickets || [];
  const pagination = activeTab === 'purchases' ? purchaseData?.data?.pagination : data?.data?.pagination;
  const allCount = allTicketsData?.data?.pagination?.total || 0;
  const winsCount = winsData?.data?.pagination?.total || 0;
  const purchasesCount = purchaseData?.data?.pagination?.total || 0;
  const purchases = purchaseData?.data?.purchases || [];
  const filteredTickets = tickets;
  
  const allTicketsForFilterData = allTicketsForFilter?.data?.tickets || [];
  const uniqueRaffles = Array.from(
    new Map(allTicketsForFilterData.filter((t: Ticket) => t.raffle).map((t: Ticket) => [t.raffleId, { id: t.raffleId, name: t.raffle!.name }])).values()
  ).sort((a, b) => a.name.localeCompare(b.name));

  const tabs = [
    { id: 'all' as TabType, label: 'All Tickets', icon: TicketIcon, count: allCount },
    { id: 'purchases' as TabType, label: 'Purchases', icon: ShoppingCart, count: purchasesCount },
    { id: 'wins' as TabType, label: 'Wins', icon: Trophy, count: winsCount },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      <Container className="py-10">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">My Tickets</h1>

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <TicketIcon className="inline w-3.5 h-3.5 mr-1" />Raffle
              </label>
              <select value={selectedRaffleId} onChange={(e) => { setSelectedRaffleId(e.target.value); setPage(1); }}
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent hover:border-gray-300 dark:hover:border-gray-600 transition-colors bg-white dark:bg-gray-900 dark:text-gray-100">
                <option value="">All Raffles</option>
                {uniqueRaffles.map((raffle: any) => (<option key={raffle.id} value={raffle.id}>{raffle.name}</option>))}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <Calendar className="inline w-3.5 h-3.5 mr-1" />Start Date
              </label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent hover:border-gray-300 dark:hover:border-gray-600 transition-colors" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <Calendar className="inline w-3.5 h-3.5 mr-1" />End Date
              </label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent hover:border-gray-300 dark:hover:border-gray-600 transition-colors" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleApplyFilters}>Apply</Button>
              {(appliedStartDate || appliedEndDate || selectedRaffleId) && (
                <Button variant="outline" onClick={handleResetFilters}><X className="w-4 h-4 mr-1" />Reset</Button>
              )}
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-100 dark:border-gray-800 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setPage(1); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}>
                <Icon className="w-4 h-4" />{tab.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Empty State */}
        {tickets.length === 0 && activeTab !== 'purchases' ? (
          <Card>
            <div className="text-center py-16">
              <Search className="h-12 w-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {activeTab === 'wins' ? 'No wins yet — keep playing' : 'No tickets yet. Browse raffles to participate!'}
              </p>
              {activeTab === 'all' && (
                <Link to="/raffles"><Button>Browse Raffles</Button></Link>
              )}
            </div>
          </Card>
        ) : (
          <>
            {/* All Tickets Tab */}
            {activeTab === 'all' && (
              <div className="space-y-3">
                {filteredTickets.map((ticket: Ticket) => (
                  <Card key={ticket.id}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Link to={`/raffles/${ticket.raffleId}`} className="hover:text-indigo-600 transition-colors truncate">
                          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{ticket.raffle?.name}</h3>
                        </Link>
                        <Badge variant={
                          ticket.status === 'WON' ? 'success' : ticket.status === 'LOST' ? 'danger' : 
                          ticket.status === 'REFUNDED' ? 'warning' : 'default'
                        }>{ticket.status}</Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5 col-span-2 md:col-span-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500">Ticket #</p>
                        <p className="font-mono font-semibold text-sm text-gray-900 dark:text-gray-100 mt-0.5 break-all">{ticket.ticketNumber}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                        <p className="text-xs text-gray-400 dark:text-gray-500">Price</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{formatCurrency(ticket.purchasePrice, ticket.raffle?.currency || 'USD')}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                        <p className="text-xs text-gray-400 dark:text-gray-500">Purchased</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{formatDate(ticket.createdAt)}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                        <p className="text-xs text-gray-400 dark:text-gray-500">Draw Date</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{ticket.drawDate ? formatDate(ticket.drawDate) : 'Pending'}</p>
                      </div>
                    </div>
                    {ticket.status === 'WON' && ticket.prizeAmount && (
                      <div className="mt-3 p-2.5 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-100 dark:border-emerald-800 rounded-xl flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <p className="text-emerald-700 dark:text-emerald-400 font-semibold text-sm">Won {formatCurrency(ticket.prizeAmount, ticket.raffle?.currency || 'USD')}</p>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}

            {/* Purchase History Tab */}
            {activeTab === 'purchases' && (
              <div className="space-y-3">
                {purchases.length === 0 ? (
                  <Card>
                    <div className="text-center py-16">
                      <ShoppingCart className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                      <p className="text-gray-500 dark:text-gray-400">No purchase history yet.</p>
                    </div>
                  </Card>
                ) : (
                  purchases.map((purchase: any) => (
                    <Card key={purchase.id}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <Link to={`/raffles/${purchase.raffleId}`} className="hover:text-indigo-600 transition-colors">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{purchase.raffleName}</h3>
                          </Link>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(purchase.transactionDate)}</p>
                        </div>
                        <p className="text-lg font-bold text-indigo-600">{formatCurrency(purchase.totalCost, purchase.currency)}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-3 mt-2 mb-3">
                        <div><p className="text-xs text-gray-400 dark:text-gray-500">Tickets</p><p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{purchase.quantity}</p></div>
                        <div><p className="text-xs text-gray-400 dark:text-gray-500">Per Ticket</p><p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{formatCurrency(purchase.totalCost / purchase.quantity, purchase.currency)}</p></div>
                        <div><p className="text-xs text-gray-400 dark:text-gray-500">Status</p><p className={`text-sm font-semibold mt-0.5 ${purchase.status === 'COMPLETED' ? 'text-emerald-600' : 'text-gray-900 dark:text-gray-100'}`}>{purchase.status}</p></div>
                      </div>
                      {purchase.ticketNumbers?.length > 0 && (
                        <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
                          <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">Ticket Numbers</p>
                          <div className="flex flex-wrap gap-1.5">
                            {purchase.ticketNumbers.slice(0, 12).map((ticketNumber: string, idx: number) => (
                              <span key={idx} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded font-mono text-xs text-gray-700 dark:text-gray-300">{ticketNumber}</span>
                            ))}
                            {purchase.ticketNumbers.length > 12 && (
                              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-semibold text-gray-500 dark:text-gray-400">+{purchase.ticketNumbers.length - 12}</span>
                            )}
                          </div>
                        </div>
                      )}
                      {purchase.tickets.some((t: any) => t.status === 'WON') && (
                        <div className="mt-3 p-2.5 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-100 dark:border-emerald-800 rounded-xl flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                          <p className="text-emerald-700 dark:text-emerald-400 font-semibold text-sm">{purchase.tickets.filter((t: any) => t.status === 'WON').length} winning ticket(s)!</p>
                        </div>
                      )}
                    </Card>
                  ))
                )}
              </div>
            )}

            {/* Wins Tab */}
            {activeTab === 'wins' && (
              <div className="space-y-3">
                {filteredTickets.length === 0 ? (
                  <Card>
                    <div className="text-center py-16">
                      <Trophy className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                      <p className="text-gray-500 dark:text-gray-400">No wins yet. Keep participating!</p>
                    </div>
                  </Card>
                ) : (
                  filteredTickets.map((ticket: Ticket) => (
                    <Card key={ticket.id}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <Trophy className="w-5 h-5 text-amber-500 shrink-0" />
                          <Link to={`/raffles/${ticket.raffleId}`} className="hover:text-indigo-600 transition-colors truncate">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{ticket.raffle?.name}</h3>
                          </Link>
                          <Badge variant="success">WINNER</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5 col-span-2 md:col-span-1">
                          <p className="text-xs text-gray-400 dark:text-gray-500">Winning Ticket</p>
                          <p className="font-mono font-semibold text-sm text-gray-900 dark:text-gray-100 mt-0.5 break-all">{ticket.ticketNumber}</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                          <p className="text-xs text-gray-400 dark:text-gray-500">Prize</p>
                          <p className="text-sm font-bold text-emerald-600 mt-0.5">{formatCurrency(ticket.prizeAmount || 0, ticket.raffle?.currency || 'USD')}</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                          <p className="text-xs text-gray-400 dark:text-gray-500">Draw Date</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{formatDate(ticket.drawDate!)}</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                          <p className="text-xs text-gray-400 dark:text-gray-500">Prize Share</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{ticket.prizePercentage ? formatPercentage(ticket.prizePercentage) : 'N/A'}</p>
                        </div>
                      </div>
                      <div className="mt-3 p-2.5 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-100 dark:border-emerald-800 rounded-xl">
                        <p className="text-sm text-emerald-600 dark:text-emerald-400">🎉 Prize credited to your wallet</p>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-8">
                <Button variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
                <span className="text-sm text-gray-500 dark:text-gray-400">Page {page} of {pagination.pages}</span>
                <Button variant="outline" disabled={page === pagination.pages} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};
